/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { Form } from 'react-final-form';
import { Config } from 'final-form';
import { StyleSheet, css } from 'aphrodite';
import { ActionButton, DomEventListener } from '@blend/enterprise-components';
import { keyCodes } from '@blend/shared-react';

import { useQuery } from 'blb-utils/login';
import { useCancelHook } from 'blb-utils/login';

import { FormCardWrapper } from './FormCardWrapper';
import { FormCardContent } from './FormCardContent';

const styles = StyleSheet.create({
  buttonWrapper: {
    ':not(:last-child)': {
      marginRight: 12,
    },
  },
  escapeContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  footerBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 28,
  },
});

export namespace FormCard {
  export interface Props<FormValues> {
    additionalFooterComponent?: JSX.Element;
    headerLabel?: JSX.Element;
    headerTitle: string;
    id: string;
    initialValues?: Partial<FormValues>;
    onSubmit: Config<FormValues, {}>['onSubmit'];
    showCancel?: boolean;
    submitLabel: string;
  }
}
export const FormCard = <FormValues,>({
  onSubmit,
  children,
  headerTitle,
  id,
  initialValues,
  submitLabel,
  headerLabel,
  showCancel = true,
  additionalFooterComponent,
}: React.PropsWithChildren<FormCard.Props<FormValues>>) => {
  const { error } = useQuery<{ error?: string }>();
  const cancelHandler = useCancelHook();
  const formattedId = `it-${id}`;
  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      {({ handleSubmit, submitError, submitting }) => {
        const displayError = submitError || error;
        return (
          <form id={formattedId} onSubmit={handleSubmit}>
            <FormCardWrapper>
              <FormCardContent
                displayError={displayError}
                formattedId={formattedId}
                headerLabel={headerLabel}
                headerTitle={headerTitle}
              >
                {children}
              </FormCardContent>
              <div className={css(styles.footerBar)}>
                {additionalFooterComponent}
                {showCancel && (
                  <div className={css(styles.buttonWrapper)}>
                    <ActionButton
                      disabled={submitting}
                      id={`${formattedId}-exit`}
                      label="Cancel"
                      onClick={cancelHandler}
                      theme="tertiary"
                    />
                  </div>
                )}
                <ActionButton
                  id={`${formattedId}-submit`}
                  isLoading={submitting}
                  label={submitLabel}
                  onClick={handleSubmit}
                />
              </div>
            </FormCardWrapper>
            <DomEventListener
              cb={async ({ keyCode }: React.KeyboardEvent<HTMLInputElement>) => {
                if (keyCode === keyCodes.ENTER) await handleSubmit(event);
              }}
              eventName="keydown"
              target={window}
            />
          </form>
        );
      }}
    </Form>
  );
};
